import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Link,
  SvgIcon,
  Typography
} from '@material-ui/core';
import DownloadIcon from '@material-ui/icons/GetApp';
import FileIcon from '@material-ui/icons/InsertDriveFileOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import Button from '../../_ui/Button/Button';
import Modal from '../../_ui/Modal/Modal';
import useSimpleModal from '../_hooks/useSimpleModal';
import { ExcelImportApi } from '../../_helpers/service';
import { ROLES_ADMIN } from '../../_helpers/Constants';
import { loginModule } from '../../pages/LoginPage/LoginDucks';
import { handleError } from '../../utils/handleError';
import OrganizationPicker from './OrganizationPicker';
import ImportResult from './ImportResult';
import { useStyles } from './ImportExcelStyle';
import './ImportExcelTranslate';

const ALLOWED_FILE = /\.(xlsx|xls)$/i;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const UploadIcon = props => (
  <SvgIcon {...props}>
    <path d="M5 20h14v-2H5v2zm0-10h4v6h6v-6h4l-7-7-7 7z" />
  </SvgIcon>
);

const ExcelFileIcon = props => (
  <SvgIcon {...props}>
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8zM14 3v5h5M9 12l6 6M15 12l-6 6"
    />
  </SvgIcon>
);

const formatSize = bytes =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} КБ`
    : `${(bytes / 1024 / 1024).toFixed(1)} МБ`;

const validateFile = file => {
  if (!ALLOWED_FILE.test(file.name)) return 'importExcel_formatError';
  if (file.size > MAX_FILE_SIZE) return 'importExcel_sizeError';
  if (file.size === 0) return 'importExcel_emptyError';
  return null;
};

// Ответ бэкенда { totalRows, loaded } приводим к { total, imported, failed, errors: [{ row, field, message }] }
const toImportResult = data => {
  const result = data || {};
  const { totalRows, loaded } = result;
  const errors = (result.errors || []).map(error => ({
    row: error.row,
    field: error.field,
    message: error.message
  }));
  const notLoaded =
    typeof totalRows === 'number' && typeof loaded === 'number'
      ? totalRows - loaded
      : errors.length;
  return {
    total: totalRows,
    imported: loaded,
    failed: typeof result.failed === 'number' ? result.failed : notLoaded,
    errors
  };
};

/**
 * Кнопка «Импортировать» с модальным окном загрузки Excel.
 * type — 'contingents' | 'staff': определяет эндпоинт импорта и шаблон.
 * onImported — вызывается при закрытии окна, если импорт был выполнен.
 */
export default function ImportExcel({ type, onImported }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const modal = useSimpleModal();
  const inputRef = useRef(null);
  const user = useSelector(state => state[loginModule].user);
  const [organization, setOrganization] = useState(null);
  const [file, setFile] = useState(null);
  const [rejected, setRejected] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [imported, setImported] = useState(false);

  const isAdmin = user.roles.some(
    role => role === ROLES_ADMIN.ES_ADMINS || role === ROLES_ADMIN.ES_GODS
  );
  if (!isAdmin) return null;

  const resetFile = () => {
    setFile(null);
    setRejected(null);
    setResult(null);
  };

  const onOpen = () => {
    resetFile();
    setOrganization(null);
    setImported(false);
    modal.open({});
  };

  const onClose = () => {
    modal.close();
    if (imported && onImported) onImported();
  };

  const selectFile = selected => {
    if (!selected) return;
    const error = validateFile(selected);
    if (error) {
      setFile(null);
      setRejected({ name: selected.name, error });
    } else {
      setFile(selected);
      setRejected(null);
    }
  };

  const onDragOver = e => {
    e.preventDefault();
    if (organization) setDragOver(true);
  };

  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    if (organization) selectFile(e.dataTransfer.files[0]);
  };

  const onUpload = async () => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('serviceProvider', organization.id);
    setProgress(0);
    setLoading(true);
    try {
      const { data } = await ExcelImportApi.importFile(type, formData, event => {
        if (event.total) setProgress(Math.round((event.loaded * 100) / event.total));
      });
      setImported(true);
      setResult(toImportResult(data));
    } catch (error) {
      handleError(error, t('importExcel_error'));
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <>
      <div className={classes.template}>
        <ExcelFileIcon className={classes.excelIcon} />
        <div className={classes.grow}>
          <Typography className={classes.medium}>{t('importExcel_templateTitle')}</Typography>
          <Typography variant="body2" color="textSecondary">
            {t('importExcel_templateHint')}
          </Typography>
        </div>
        <Link
          href={`${process.env.PUBLIC_URL}/templates/import_${type}.xlsx`}
          download={`${t(`importExcel_templateName_${type}`)}.xlsx`}
          underline="always"
          className={classes.link}
        >
          <DownloadIcon fontSize="small" />
          {t('importExcel_downloadTemplate')}
        </Link>
      </div>

      <OrganizationPicker
        value={organization}
        onChange={org => {
          setOrganization(org);
          if (!org) resetFile();
        }}
      />

      {organization ? (
        <div
          className={`${classes.dropzone} ${dragOver ? classes.dropzoneActive : ''}`}
          onDragOver={onDragOver}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <UploadIcon className={classes.uploadIcon} />
          <Typography>{t('importExcel_dropHint')}</Typography>
          <Button
            variant="outlined"
            text={t('importExcel_chooseFile')}
            onClick={() => inputRef.current.click()}
          />
          <Typography variant="body2" color="textSecondary">
            {t('importExcel_formats')}
          </Typography>
          <input
            ref={inputRef}
            type="file"
            className="display-none"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={({ target }) => {
              selectFile(target.files[0]);
              target.value = '';
            }}
          />
        </div>
      ) : (
        <div className={`${classes.dropzone} ${classes.dropzoneDisabled}`} aria-disabled="true">
          <Typography color="inherit">{t('importExcel_selectOrganizationFirst')}</Typography>
          <Typography variant="body2" color="inherit">
            {t('importExcel_selectOrganizationHint')}
          </Typography>
        </div>
      )}

      {file && (
        <div className={classes.fileCard}>
          <FileIcon className={classes.successIcon} />
          <div className={classes.grow}>
            <Typography noWrap className={classes.medium}>
              {file.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatSize(file.size)}
            </Typography>
          </div>
          <IconButton aria-label={t('importExcel_removeFile')} onClick={() => setFile(null)}>
            <DeleteIcon />
          </IconButton>
        </div>
      )}

      {rejected && (
        <div className={classes.errorCard} role="alert">
          <ErrorIcon />
          <div className={classes.grow}>
            <Typography noWrap color="inherit" className={classes.medium}>
              {rejected.name}
            </Typography>
            <Typography variant="body2" color="inherit">
              {t(rejected.error)}
            </Typography>
          </div>
          <IconButton
            color="inherit"
            aria-label={t('importExcel_removeFile')}
            onClick={() => setRejected(null)}
          >
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </>
  );

  const renderProgress = () => (
    <div className={classes.progress}>
      <div>
        <div className={classes.progressHeader}>
          {progress < 100 ? (
            <CircularProgress size={18} />
          ) : (
            <DoneIcon fontSize="small" className={classes.successIcon} />
          )}
          <Typography className={`${classes.grow} ${classes.medium}`}>
            {t('importExcel_sending')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {progress}%
          </Typography>
        </div>
        <LinearProgress variant="determinate" value={progress} className={classes.progressBar} />
        <Typography variant="body2" color="textSecondary">
          {file.name} · {formatSize(file.size)}
        </Typography>
      </div>
      {progress >= 100 && (
        <div>
          <div className={classes.progressHeader}>
            <CircularProgress size={18} />
            <Typography className={`${classes.grow} ${classes.medium}`}>
              {t('importExcel_processing')}
            </Typography>
          </div>
          <LinearProgress className={classes.progressBar} />
          <Typography variant="body2" color="textSecondary">
            {t('importExcel_processingHint')}
          </Typography>
        </div>
      )}
    </div>
  );

  const renderActions = () => {
    if (result) {
      return (
        <>
          <Button
            variant="outlined"
            text={t('importExcel_uploadAnother')}
            onClick={resetFile}
          />
          <Button text={t('importExcel_close')} onClick={onClose} />
        </>
      );
    }
    return (
      <>
        <Button
          variant="text"
          color="primary"
          disabled={loading}
          text={t('importExcel_cancel')}
          onClick={onClose}
        />
        <Button
          disabled={loading || !organization || !file}
          text={loading ? t('importExcel_uploading') : t('importExcel_upload')}
          onClick={onUpload}
        />
      </>
    );
  };

  return (
    <>
      <Button
        text={t('importExcel_btn')}
        icon={<UploadIcon fontSize="small" className="mr1" />}
        onClick={onOpen}
        className={classes.importButton}
      />
      {modal.isOpen && (
        <Modal
          open
          classes={{ paper: classes.paper }}
          onClose={loading ? undefined : onClose}
          title={result ? t('importExcel_finished') : t(`importExcel_title_${type}`)}
        >
          <DialogContent dividers className={classes.content}>
            {loading && renderProgress()}
            {!loading && result && <ImportResult organization={organization} result={result} />}
            {!loading && !result && renderForm()}
          </DialogContent>
          <DialogActions className={classes.actions}>{renderActions()}</DialogActions>
        </Modal>
      )}
    </>
  );
}
